/**
 * The 3D model: the 3D viewer, the markup extension, and comments anchored to
 * elements plus the camera they were seen from.
 *
 * A 3D comment that reopens on a default view is useless — "the valve is wrong" means
 * nothing without the valve on screen. So every 3D mark-up stores the element dbIds AND
 * the viewer state, and selecting it restores the camera first and then isolates the
 * elements. Properties are read from the SERVER-SIDE property database, not from the
 * browser's partially-loaded model, so two users inspecting the same element see the same
 * answer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Crosshair, Eye, EyeOff, Layers3, Loader2, Move3d, Ruler, Scissors } from 'lucide-react';
import api from '@/lib/api';
import { useForgeViewer } from '@/hooks/useForgeViewer';

const EXTENSIONS = [
  'Autodesk.Section',
  'Autodesk.Explode',
  'Autodesk.Measure',
  'Autodesk.Viewing.MarkupsCore',
];

const Btn = ({ onClick, children, testid, active, disabled, title }) => (
  <button onClick={onClick} disabled={disabled} title={title} data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs border transition-colors
      ${active ? 'bg-amber-500/15 border-amber-500/40 text-amber-300'
      : 'border-white/10 hover:bg-white/5'} disabled:opacity-40`}>
    {children}
  </button>
);

export default function Edm3dModel({ projectId, urn, fileId, region, markups, visibleLayers,
  selectedId, onSelectMarkup, onCommit }) {
  const [sel, setSel] = useState([]);
  const [props, setProps] = useState(null);
  const [propsBusy, setPropsBusy] = useState(false);
  const [explode, setExplode] = useState(0);
  const [sketching, setSketching] = useState(false);
  const [msg, setMsg] = useState(null);
  const markupsExtRef = useRef(null);

  const onModelLoaded = useCallback((v) => {
    v.addEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, (e) => {
      setSel(e.dbIdArray || []);
    });
    v.loadExtension('Autodesk.Viewing.MarkupsCore')
      .then((ext) => { markupsExtRef.current = ext; })
      .catch(() => { markupsExtRef.current = null; });
  }, []);

  const { containerRef, viewer, status, error } = useForgeViewer({
    projectId,
    urn,
    tokenUrl: urn ? `/edm/aps/viewer-token?urn=${encodeURIComponent(urn)}` : null,
    extensions: EXTENSIONS,
    region,
    onModelLoaded,
  });

  // Restore the view a comment was made from, then isolate what it is about.
  useEffect(() => {
    if (!viewer || !selectedId) return;
    const m = (markups || []).find((x) => x.comment_id === selectedId && x.dimension === '3d');
    if (!m) return;
    const state = m.anchor?.viewerState;
    if (state) { try { viewer.restoreState(state); } catch { /* a stale state is not fatal */ } }
    const ids = m.anchor?.dbIds || [];
    if (ids.length) { viewer.isolate(ids); viewer.fitToView(ids); }
    const svgEnt = (m.entities || []).find((e) => e.type === 'sketch3d' && e.svg);
    if (svgEnt && markupsExtRef.current) {
      try {
        markupsExtRef.current.show();
        markupsExtRef.current.loadMarkups(svgEnt.svg, `edm-${m.comment_id}`);
      } catch { /* the extension refusing a layer is not fatal */ }
    }
  }, [viewer, selectedId, markups]);

  const inspect = useCallback(async () => {
    if (!sel.length || !fileId) return;
    setPropsBusy(true); setProps(null);
    try {
      const r = await api.get(`/edm/files/${fileId}/properties?dbIds=${sel.join(',')}`);
      setProps(r.data);
    } catch (e) {
      setProps({ error: e?.response?.data?.detail?.why || e?.response?.data?.detail
        || 'The property database could not be read.' });
    } finally { setPropsBusy(false); }
  }, [sel, fileId]);

  const attach = useCallback(async () => {
    if (!viewer) return;
    if (!sel.length) { setMsg('Select the element(s) the comment is about first.'); return; }
    await onCommit({
      dimension: '3d',
      entities: [{ type: 'element', dbIds: sel }],
      viewerState: viewer.getState(),
      modelUrn: urn,
    });
    setMsg(null);
  }, [viewer, sel, onCommit, urn]);

  const sketch = useCallback(async () => {
    const ext = markupsExtRef.current;
    if (!ext || !viewer) { setMsg('The 3D model service markup extension is not loaded yet.'); return; }
    if (!sketching) {
      ext.enterEditMode();
      setSketching(true);
      setMsg('Sketching on this view. Draw, then press Save sketch — the camera is frozen '
        + 'and stored with it.');
      return;
    }
    const svg = ext.generateData();
    ext.leaveEditMode();
    ext.hide();
    setSketching(false);
    if (!svg) { setMsg('Nothing was drawn, so nothing was saved.'); return; }
    await onCommit({
      dimension: '3d',
      entities: [{ type: 'sketch3d', svg }, ...(sel.length ? [{ type: 'element', dbIds: sel }] : [])],
      viewerState: viewer.getState(),
      modelUrn: urn,
    });
    setMsg(null);
  }, [sketching, viewer, sel, onCommit, urn]);

  const shown3d = (markups || []).filter(
    (m) => m.dimension === '3d' && (visibleLayers === null || visibleLayers.has(m.layerId)));

  return (
    <div data-testid="edm-3d-model">
      <div className="flex items-center gap-1.5 flex-wrap px-2 py-1.5 border-b"
        style={{ borderColor: 'var(--border-color)' }}>
        <Btn testid="edm-3d-isolate" onClick={() => viewer?.isolate(sel)} disabled={!sel.length}
          title="Isolate the selected elements"><Box className="w-3.5 h-3.5" /> Isolate</Btn>
        <Btn testid="edm-3d-hide" onClick={() => viewer?.hide(sel)} disabled={!sel.length}
          title="Hide the selected elements"><EyeOff className="w-3.5 h-3.5" /> Hide</Btn>
        <Btn testid="edm-3d-showall" onClick={() => { viewer?.isolate([]); viewer?.showAll(); }}
          title="Show everything again"><Eye className="w-3.5 h-3.5" /> Show all</Btn>
        <Btn testid="edm-3d-section"
          onClick={() => viewer?.getExtension('Autodesk.Section')?.activate('x')}
          title="Section plane"><Scissors className="w-3.5 h-3.5" /> Section</Btn>
        <Btn testid="edm-3d-measure"
          onClick={() => viewer?.getExtension('Autodesk.Measure')?.activate('distance')}
          title="Measure in the model"><Ruler className="w-3.5 h-3.5" /> Measure</Btn>
        <label className="inline-flex items-center gap-1.5 text-xs px-2">
          <Move3d className="w-3.5 h-3.5" /> Explode
          <input type="range" min="0" max="1" step="0.05" value={explode}
            data-testid="edm-3d-explode"
            onChange={(e) => {
              const v = Number(e.target.value); setExplode(v); viewer?.explode(v);
            }} className="w-20" />
        </label>
        <span className="mx-1 opacity-20">|</span>
        <Btn testid="edm-3d-inspect" onClick={inspect} disabled={!sel.length || propsBusy}
          title="Element properties from the server-side property database">
          {propsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Crosshair className="w-3.5 h-3.5" />} Properties
        </Btn>
        <Btn testid="edm-3d-attach-comment" onClick={attach} disabled={!sel.length}
          title="Attach a comment to the selected elements with this camera">
          <Layers3 className="w-3.5 h-3.5" /> Comment on selection
        </Btn>
        <Btn testid="edm-3d-sketch" onClick={sketch} active={sketching}
          title="Sketch over this view with the markup extension">
          {sketching ? 'Save sketch' : 'Sketch on view'}
        </Btn>
        <span className="text-[11px] ml-1" style={{ color: 'var(--text-muted)' }}
          data-testid="edm-3d-selection-count">
          {sel.length ? `${sel.length} element(s) selected` : 'nothing selected'}
          {shown3d.length ? ` · ${shown3d.length} 3D mark-up(s) on this revision` : ''}
        </span>
      </div>

      {msg && (
        <div className="px-3 py-2 text-[12px] text-amber-300 bg-amber-500/5 border-b border-amber-500/20"
          data-testid="edm-3d-msg">{msg}</div>
      )}

      <div className="relative" style={{ height: '62vh', background: '#0e1216' }}>
        <div ref={containerRef} className="absolute inset-0" data-testid="edm-3d-canvas" />
        {status !== 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-steel-300"
            data-testid="edm-3d-status">
            {error ? <span className="text-red-400">{error}</span>
              : <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {status}
              </span>}
          </div>
        )}
      </div>

      {props && (
        <div className="p-3 border-t text-[12px]" style={{ borderColor: 'var(--border-color)' }}
          data-testid="edm-3d-properties">
          {props.error ? <span className="text-red-400">{props.error}</span> : (
            <>
              <div className="text-[11px] mb-1.5" style={{ color: 'var(--text-muted)' }}>
                {props.found} of {props.requestedDbIds?.length} element(s) found · {props.provenance}
              </div>
              {(props.elements || []).slice(0, 4).map((el) => (
                <div key={el.dbId} className="mb-2">
                  <div className="font-medium">{el.name || `dbId ${el.dbId}`}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1">
                    {Object.entries(el.properties || {}).slice(0, 12).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3">
                        <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                        <span className="truncate">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(props.missing || []).length > 0 && (
                <div className="text-[11px] text-amber-400">
                  No property record for dbId {props.missing.join(', ')} — the extracted
                  database does not cover {props.missing.length} of the selected element(s).
                </div>
              )}
            </>
          )}
        </div>
      )}

      {shown3d.length > 0 && (
        <div className="p-2 border-t flex flex-wrap gap-1.5" style={{ borderColor: 'var(--border-color)' }}
          data-testid="edm-3d-markup-chips">
          {shown3d.map((m) => (
            <button key={m.comment_id} onClick={() => onSelectMarkup(m.comment_id)}
              data-testid={`edm-3d-markup-chip-${m.comment_id}`}
              className={`text-[11px] px-2 py-1 rounded border ${selectedId === m.comment_id
                ? 'border-amber-400/60' : 'border-white/10'}`}
              style={{ opacity: m.render?.opacity ?? 1, borderLeft: `3px solid ${m.colour}` }}>
              #{m.comment_no} {m.reviewer}
              {m.render?.label ? ` · ${m.render.label}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
